import { useState } from "react";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";

/**
 * Newsletter signup. Variants: "block" (big on blog page), "band" (home page bottom band).
 */
export default function NewsletterSignup({ variant = "block", testid = "newsletter-signup" }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const r = await subscribeNewsletter(email.trim().toLowerCase());
      setDone(true);
      if (r.status === "already_subscribed") {
        toast.success("You're already on the list. See you in the next one 💜");
      } else {
        toast.success("You're in! Check your inbox for a welcome email.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Subscribe failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (variant === "band") {
    return (
      <section className="bg-paper-100 border-y border-paper-200" data-testid={testid}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="overline text-terracotta">The STEM Vet newsletter</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-ink mt-3 leading-tight">
              Real advice, straight to your inbox.
            </h2>
            <p className="mt-4 text-ink-muted leading-relaxed max-w-md">
              Study tips, MMI prep notes, and first-gen admissions reality. Sent when I have something worth saying.
            </p>
          </div>
          {done ? (
            <div className="bg-forest text-paper-50 p-7 rounded-sm flex items-start gap-3" data-testid={`${testid}-success`}>
              <CheckCircle2 className="w-6 h-6 text-scholar shrink-0 mt-0.5" strokeWidth={1.8} />
              <div>
                <p className="font-display font-bold text-lg">You're in.</p>
                <p className="text-sm text-paper-200/90 mt-1">Check your inbox for a welcome email.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid={`${testid}-form`}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                data-testid={`${testid}-email`}
                className="flex-1 bg-paper-50 border border-paper-200 px-4 py-3.5 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid={`${testid}-submit`}
                className="inline-flex items-center justify-center gap-2 bg-forest text-paper-50 px-6 py-3.5 text-sm font-semibold rounded-sm hover:bg-forest-700 transition-colors disabled:opacity-60"
              >
                {busy ? "Joining…" : <>Subscribe <Send className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>
      </section>
    );
  }

  // block (blog page)
  return (
    <section
      className="bg-ink text-paper-50 rounded-sm p-8 sm:p-10"
      data-testid={testid}
    >
      <div className="flex items-center gap-3 mb-5">
        <Mail className="w-7 h-7 text-scholar" strokeWidth={1.6} />
        <p className="overline text-scholar">Subscribe</p>
      </div>
      <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-tight">
        New essays, straight to your inbox.
      </h3>
      <p className="mt-3 text-paper-200/80 leading-relaxed max-w-lg">
        Honest notes on STEM, MMIs, and first-gen admissions. I only send when I have something
        worth reading. Unsubscribe anytime.
      </p>
      {done ? (
        <div className="mt-7 bg-paper-50/10 border border-paper-50/20 p-5 rounded-sm flex items-start gap-3" data-testid={`${testid}-success`}>
          <CheckCircle2 className="w-5 h-5 text-scholar shrink-0 mt-0.5" />
          <div className="text-sm text-paper-200/90">
            <p className="font-semibold text-paper-50">You're in 💜</p>
            <p>Check your inbox for a welcome email from Dr. Delmar.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row gap-3" data-testid={`${testid}-form`}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            required
            data-testid={`${testid}-email`}
            className="flex-1 bg-paper-50 border border-paper-200 text-ink px-4 py-3.5 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-scholar"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid={`${testid}-submit`}
            className="inline-flex items-center justify-center gap-2 bg-scholar text-ink px-6 py-3.5 text-sm font-semibold rounded-sm hover:bg-scholar-dark transition-colors disabled:opacity-60"
          >
            {busy ? "Joining…" : <>Subscribe <Send className="w-4 h-4" /></>}
          </button>
        </form>
      )}
    </section>
  );
}
